import { palette, spaces } from '@kimuno/theme';
import { Box, DialogContent, List, Typography } from '@mui/material';
import { format as formatDate } from 'date-fns';
import { de } from 'date-fns/locale';
import type React from 'react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cancelSubscription as cancelSubscriptionAction } from '../../../actions/user.actions';
import CancelIcon from '../../../assets/subscription/cancel.svg';
import {
	cancellationReasons,
	manageSubscriptionSteps,
	subscriptionDateFormat,
} from '../../../constants';
import { NotificationContext } from '../../../state/NotificationContextProvider';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import useAction from '../../../utils/useAction';
import useScreenSize from '../../../utils/useScreenSize';
import DialogActions from '../../common/DialogActions';
import OptionItem from '../../onboarding/OnboardingOptionItem';
import type { DialogStep } from '../ManageSubscriptionDialog';

interface CancelSubscriptionDialogContentProps {
	setStep: (step: DialogStep) => void;
	onClose: () => void;
	endDate: string;
}
const CancelSubscriptionDialogContent: React.FC<CancelSubscriptionDialogContentProps> = (props) => {
	const { t } = useTranslation();
	const { setStep, onClose, endDate } = props;
	const { showNotification } = useContext(NotificationContext);
	const { isXsScreen, isSmScreen } = useScreenSize();
	const [option, setOption] = useState<string>('');
	const [isLoading, setIsLoading] = useState(false);
	const cancelSubscription = useAction(cancelSubscriptionAction);
	const subscriptionEndDate = formatDate(new Date(endDate), subscriptionDateFormat, { locale: de });

	const handleCancelSubscription = async () => {
		try {
			setIsLoading(true);
			await cancelSubscription({ reason: t(option) });
			showNotification({
				message: t('Your cancellation was successful'),
				description: t(
					'We have received your cancellation. Your access will remain active until {{endDate}}. ' +
						'– until then, you can continue to use meinUnterricht to its full potential',
					{ endDate: subscriptionEndDate }
				),
				severity: 'success',
			});
		} catch (error) {
			showNotification({
				message: t('Your cancellation was not successful'),
				severity: 'error',
			});
			notifyBugsnagHandledError(error as Error);
		} finally {
			onClose();
		}
	};

	return (
		<>
			<DialogContent sx={{ p: 0 }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: isXsScreen || isSmScreen ? 'column' : 'row',
						alignItems: 'flex-start',
						flex: '1 0 0',
						alignSelf: 'stretch',
					}}
				>
					{!isXsScreen && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								alignSelf: isSmScreen ? 'center' : 'flex-start',
								'& svg': {
									width: '400px',
									height: '300px',
								},
							}}
						>
							<CancelIcon />
						</Box>
					)}

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							padding: spaces.lg,
							justifyContent: 'center',
							alignItems: 'flex-start',
							gap: spaces.lg,
							flex: '1 0 0',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: spaces.xs,
							}}
						>
							<Typography variant="h6" sx={{ color: palette.text.primary }}>
								{t('Too bad you want to leave!')}
							</Typography>
							<Typography variant="body2" sx={{ color: palette.text.secondary }}>
								{t(
									'Your feedback helps us make myLessons even better. What is the main reason for your resignation?'
								)}
							</Typography>
						</Box>
						<List
							dense
							sx={{
								padding: 0,
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
								gap: spaces.md,
							}}
						>
							{cancellationReasons.map((reason) => (
								<OptionItem
									key={reason}
									label={reason}
									isSelected={reason === option}
									isRadioButton={true}
									onClick={() => setOption(reason)}
									sxProps={{ padding: spaces.md }}
								/>
							))}
						</List>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions
				onBack={() => setStep(manageSubscriptionSteps.manage)}
				primaryButton={{
					text: t('Cancel subscription'),
					color: 'error',
					variant: 'contained',
					onClick: handleCancelSubscription,
					loading: isLoading,
				}}
				secondaryButton={{
					text: t('Cancel'),
					onClick: onClose,
				}}
			/>
		</>
	);
};

export default CancelSubscriptionDialogContent;
